import styled from "styled-components";
import {Button, Typography} from "@mui/material";
import {radii, type ModeTokens} from "../theme/tokens";

/**
 * Styles for the verify-your-email callout (ui#1458), externalized per the repo's styling convention
 * so the component file stays markup + behavior.
 *
 * TWO TONES, and they carry the whole state:
 *
 * - `prompt` — BLUE (owner, 2026-08-24: "the container should be blue", second polish round on
 *   ui#1488). Informational, not a warning: under kitecorp/transferify-api-service#832 the account is
 *   fully functional and this callout gates nothing, so the amber it wore until now overstated the
 *   situation. `accentTint` + `blue` is this app's informational pair — the same one `NavBar`'s active
 *   pill uses, rather than a colour picked for this one surface.
 * - `sent` — TEAL. The mail is out and nothing is owed.
 *
 * Both accents are now per-mode `tokens`, so dark mode brightens the hairline and deepens the tint
 * with no branch here; the amber it replaced was the mode-independent `brand.warning`, which is why
 * this file no longer imports `brand` at all.
 *
 * The 12px radius and the 12px/14px padding are the notices slot's box, shared with the offer callout
 * beside it: a box that did not match would read as a different kind of thing.
 */
export type CalloutTone = "prompt" | "sent";

/** The tone's accent — hairline and control edge. Paired with {@link tint} below; never split them. */
const accent = (tone: CalloutTone, tokens: ModeTokens) => (tone === "sent" ? tokens.teal : tokens.blue);

/** The tone's translucent surface, the low-alpha wash of the same accent (see `tokens.ts`). */
const tint = (tone: CalloutTone, tokens: ModeTokens) =>
	tone === "sent" ? tokens.signalTint : tokens.accentTint;

export const Root = styled.div<{$tone: CalloutTone}>`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px 12px;
	padding: 12px 14px;
	border-radius: 12px;
	border: 1px solid ${({$tone, theme}) => accent($tone, theme.tokens)}55;
	background: ${({$tone, theme}) => tint($tone, theme.tokens)};
	color: ${({theme}) => theme.tokens.ink};
`;

/**
 * The copy column. The 12rem BASIS is what makes the row reflow rather than squeeze at 375px: the
 * sentence keeps a readable measure and the control wraps beneath it.
 *
 * `min-width: 0`, not a matching 12rem floor. A basis is a preference the container may shrink below;
 * a min-width is a rule it may not, and at a 24px root font 12rem is 288px against roughly 280px of
 * usable width inside the panel at 375px — so the floor could push the row past its container on the
 * one viewport this layout exists to serve.
 */
export const Copy = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	flex: 1 1 12rem;
	min-width: 0;
`;

export const Message = styled(Typography)`
	color: ${({theme}) => theme.tokens.ink};
`;

/** A send failure: one step quieter than the message. */
export const Detail = styled(Typography)`
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * Outlined rather than filled: this is an offer, not the app asking to be rescued, and the panel's
 * one FILLED control is the send button the sender came here to press. Blue, matching the tone the
 * prompt state wears — it only ever renders in that state.
 *
 * The hover deepens the surface by laying a SECOND `accentTint` over the Root's own, which is a real
 * step in both modes because the tint is translucent. A `blue` fill would be the wrong press for an
 * outlined control, and `blued` is the hover for a blue FILL, not for a tint.
 */
export const VerifyButton = styled(Button)`
	border-color: ${({theme}) => theme.tokens.blue};
	color: ${({theme}) => theme.tokens.ink};
	border-radius: ${radii.control};
	white-space: nowrap;
	&:hover {
		border-color: ${({theme}) => theme.tokens.blue};
		background-color: ${({theme}) => theme.tokens.accentTint};
	}
`;
